import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../../lib/animations';
import AssessmentLayout from './AssessmentLayout';
import heartIcon from '../../../assets/icons/pre-assesment-heart.svg';
import growthIcon from '../../../assets/icons/pre-assesment-growth.svg';
import clockIcon from '../../../assets/icons/pre-assesment-clock.svg';
import closeIcon from '../../../assets/icons/pre-assesment-close.svg';

interface PreAssessmentScreenProps {
  onStartCheckIn: () => void;
  onDoThisLater: () => void;
}

const BENEFITS = [
  {
    icon: heartIcon,
    hasBackground: false,
    title: 'No wrong answers',
    description: 'Every child develops at their own pace. Just share what you notice day to day.',
  },
  {
    icon: growthIcon,
    hasBackground: true,
    title: 'Adapts as your child does',
    description: "We find the sparks and build a learning plan that supports every next step.",
  },
  {
    icon: clockIcon,
    hasBackground: false,
    title: 'Takes 2-3 minutes',
    description: 'Quick and easy. You can save and come back anytime.',
  },
] as const;

export default function PreAssessmentScreen({ onStartCheckIn, onDoThisLater }: PreAssessmentScreenProps) {
  return (
    <AssessmentLayout backgroundColor="#DBF7F8">
      <motion.div
        className="flex flex-col py-6 min-[412px]:pt-12 min-[430px]:pt-16 md:pt-14 lg:pt-16"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants} className="flex items-center justify-between mb-6">
          <span
            className="font-black text-[20px] text-[#007CAD] tracking-[-1.2px] whitespace-nowrap shrink-0"
            style={{ fontVariationSettings: "'opsz' 14" }}
          >
            Play Moments
          </span>
          <button
            type="button"
            onClick={onDoThisLater}
            className="w-12 h-12 flex items-center justify-center"
            aria-label="Close"
          >
            <img src={closeIcon} alt="" className="w-6 h-6" />
          </button>
        </motion.div>

        <motion.h1
          variants={itemVariants}
          className="font-bold text-[32px] md:text-[34px] lg:text-[36px] text-[#1B2831] tracking-[-1.28px] leading-none text-center"
          style={{ fontVariationSettings: "'opsz' 14" }}
        >
          Every child develops differently. 
        </motion.h1>

        <motion.p
          variants={itemVariants}
          className="mt-6 text-[16px] md:text-[18px] lg:text-[20px] text-[#1B2831] text-center leading-none"
          style={{ fontVariationSettings: "'opsz' 14" }}
        >
          A few quick questions help us understand yours, covering speech, language, and social-emotional development, designed with NHS Speech and Language Therapists and early years experts.
        </motion.p>

        <div className="flex flex-col gap-9 mt-10 min-[412px]:mt-12 min-[430px]:mt-14 md:mt-14">
          {BENEFITS.map((benefit) => (
            <motion.div key={benefit.title} variants={itemVariants} className="flex gap-5 items-center">
              {benefit.hasBackground ? (
                <div className="w-12 h-12 rounded-full bg-[#C7EDEF] flex items-center justify-center shrink-0 overflow-hidden">
                  <img src={benefit.icon} alt="" className="w-[31px] h-[30px]" />
                </div>
              ) : (
                <img src={benefit.icon} alt="" className="w-12 h-12 shrink-0" />
              )}
              <div className="flex flex-col gap-2">
                <span
                  className="font-bold text-[18px] md:text-[20px] lg:text-[22px] text-[#1B2831] tracking-[-0.72px]"
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  {benefit.title}
                </span>
                <span
                  className="text-[16px] md:text-[18px] lg:text-[20px] text-[#1B2831]"
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  {benefit.description}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div variants={itemVariants} className="flex flex-col gap-4 mt-10 min-[412px]:mt-16 min-[430px]:mt-18 md:mt-18">
          <button
            type="button"
            onClick={onStartCheckIn}
            className="w-full rounded-lg bg-[#1B2831] py-4 flex items-center justify-center"
          >
            <span
              className="text-white font-black text-xs uppercase tracking-[0.72px] whitespace-pre"
              style={{ fontVariationSettings: "'opsz' 14" }}
            >
              Start check in
            </span>
          </button>
          <button
            type="button"
            onClick={onDoThisLater}
            className="w-full rounded-lg border-2 border-[#1B2831] py-4 flex items-center justify-center"
          >
            <span
              className="text-[#1B2831] font-black text-xs uppercase tracking-[0.72px] whitespace-nowrap"
              style={{ fontVariationSettings: "'opsz' 14" }}
            >
              Do this later
            </span>
          </button>
        </motion.div>
      </motion.div>
    </AssessmentLayout>
  );
}
